import { z } from 'zod';
import { ConfigurationGroupType, SpaPackageType } from 'shared-types/SpaPackage';

const selectionDataSchema = z.object({
  configurationGroupId: z.number(),
  optionId: z.number(),
  isSelected: z.boolean(),
  timeslot: z.object({ startTime: z.string(), endTime: z.string() }).nullable().optional(),
});

const guestSchema = z.object({
  title: z.string().trim().min(1, 'required'),
  firstName: z.string().trim().min(1, 'required'),
  lastName: z.string().trim().min(1, 'required'),
  gender: z.string().trim().min(1, 'required'),
  id: z.union([z.string(), z.number()]).optional(),
  loggedInUser: z.any().optional(),
  externalSpaId: z.number().optional(),
  placeholder: z.boolean().optional(),
  configurationGroups: z.record(z.string(), z.array(selectionDataSchema.nullish())).optional(),
});

const isTimeslotRequired = (
  selection: { isSelected: boolean; timeslot?: { startTime: string; endTime: string } | null },
  group: ConfigurationGroupType,
  configurationGroupsRequireTimeslots: boolean
): boolean => {
  if (!configurationGroupsRequireTimeslots) return false;

  // If the group doesn't support timeslots, no timeslot is required
  if (!group.supportsTimeslots) return false;

  // If the group supports selections but the option is not selected, no timeslot needed
  if (group.supportsSelections && !selection.isSelected) return false;

  // If the group supports selections but not timeslots, no timeslot needed
  if (!group.supportsTimeslots && group.supportsSelections) return false;

  // Timeslot is required: either the group doesn't support selections (all need timeslots)
  // or the option is selected and needs a timeslot
  return true;
};

export const buildSpaPackageFormSchema = (entity: SpaPackageType) =>
  z
    .object({
      date: z.string().min(1, 'required'),
      arrivalTime: z
        .object({
          startTime: z.string(),
          endTime: z.string(),
          discount: z.any().optional(),
          price: z.any().optional(),
        })
        .optional(),
      guests: z.array(guestSchema),
      note: z.string().optional(),
      extras: z.record(z.string(), z.any()).optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.guests || data.guests.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'required',
          path: ['guests'],
        });
        return;
      }

      const requiredGroups = entity.configurationGroups.filter(
        (cg) => !cg.isExtras && cg.isRequired
      );

      data.guests.forEach((guest, guestIndex) => {
        // Validate configuration group selections and timeslots
        for (const group of requiredGroups) {
          const groupKey = `group_${group.id}`;
          const selections = (guest.configurationGroups?.[groupKey] ?? []).filter(
            (s): s is NonNullable<typeof s> => s != null
          );

          // Check minimumSelections
          if (group.minimumSelections) {
            const selectedCount = selections.filter((s) => s.isSelected).length;
            if (selectedCount < group.minimumSelections) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'required',
                path: ['guests', guestIndex, 'configurationGroups', groupKey],
              });
            }
          }

          // Check timeslots on each selection
          selections.forEach((selection, selectionIndex) => {
            if (
              isTimeslotRequired(selection, group, entity.configurationGroupsRequireTimeslots) &&
              !selection.timeslot
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'selectionRequired',
                path: ['guests', guestIndex, 'configurationGroups', groupKey, selectionIndex],
              });
            }
          });
        }
      });
    });

export type SpaPackageFormValues = z.infer<ReturnType<typeof buildSpaPackageFormSchema>>;
